import { cn } from '@/lib/utils';
import type { BadgeKind } from '@/lib/cobalt-helpers';

interface Props {
  kind?: BadgeKind;
  dot?: boolean;
  className?: string;
  children: React.ReactNode;
}

/**
 * Semantic status chip. Pairs a tinted background with a solid text color
 * at AA contrast. The optional `dot` adds a small dot in the foreground hue.
 *
 * Use this anywhere status, stage, or other categorical labels render.
 */
export function StatusBadge({ kind = 'neutral', dot, className, children }: Props) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5',
        'text-[11.5px] font-medium leading-tight tracking-[0.01em]',
        kindClass(kind),
        className,
      )}
    >
      {dot && <span className={cn('h-1.5 w-1.5 rounded-full', dotClass(kind))} />}
      {children}
    </span>
  );
}

function kindClass(k: BadgeKind): string {
  switch (k) {
    case 'brand':       return 'bg-brand-soft text-brand-strong dark:text-brand';
    case 'info':        return 'bg-info-soft text-info dark:text-info';
    case 'success':     return 'bg-success-soft text-success dark:text-success';
    case 'warning':     return 'bg-warning-soft text-warning dark:text-warning';
    case 'destructive': return 'bg-destructive-soft text-destructive dark:text-destructive';
    case 'neutral':
    default:            return 'bg-neutral-soft text-neutral dark:text-muted-foreground';
  }
}

function dotClass(k: BadgeKind): string {
  switch (k) {
    case 'brand':       return 'bg-brand dark:bg-brand';
    case 'info':        return 'bg-info';
    case 'success':     return 'bg-success';
    case 'warning':     return 'bg-warning';
    case 'destructive': return 'bg-destructive';
    case 'neutral':
    default:            return 'bg-neutral';
  }
}
